import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from './store';
import http from '../utils/http';
import { DateTime } from 'luxon';
import { logerUser } from './UsersSlice';
import { EntityAuthorization } from './EntitySlice';
import { DocumentReference } from './DocumentsSlice';


export interface DatabaseEntityContact {
    entityCode: string | null;
    entityContactId: number | null;
    title: string | null;
    startDate: string | null;
    endDate: string | null;    
    logerUserId: number | null;
    userName: string | null;
    firstName: string | null;
    lastName: string | null;
    contactInfoId: number | null;
    email: string | null;
    phoneNumber: string | null;
    extension: string | null;
    addressId: number | null;
    cityCode: string | null;
    street: string | null;
    pobox: string | null;
    zip: string | null;
    entityAuthorizations: Array<EntityAuthorization>
}


export interface ContactInfo {
    number: Number;
    email: string;
}

export interface Number {
    number: string;
    extension: string;
}

export interface ContactInfoInput {
    phoneNumber: string;
    email: string;
    extension: number | null;
}

interface entity {
    code: string;
    name: string;
}

interface user {
    login: string;
    firstName: string;
    lastName: string;
}

export interface EntityUserAuthorization extends EntityAuthorization {
    entity: entity;
    user: user;
    title?: string | null;
    phoneNumber?: string | null;
    extension?: string | null;
}

export enum EntityContactRole {
    PRIMARY = "PRIMARY",
    SECONDARY = "SECONDARY",
    AUDITOR = "AUDITOR",
    MANAGEMENT_COMPANY = "MANAGEMENT_COMPANY",
    OTHER = "OTHER",
    CONTACT = "CONTACT"
}

export enum EntityUserRole {
    EDITOR = "EDITOR",
    THIRD_PARTY = "THIRD_PARTY",
    NOTIFICATION = "NOTIFICATION",
}

export enum ReportAccessEnum {
    AFR = "AFR",
    TWENTY_NINE = "COURT_REPORTING_29",
    NINE_THIRTY_NINE = "COURT_REPORTING_939",
}

export interface EntityContactState {
    databaseContacts: DatabaseEntityContact[];
    currentEntityCode: string;
    status: 'idle' | 'loading' | 'failed';
}

const initialState: EntityContactState = {
    databaseContacts: Array<DatabaseEntityContact>(),
    currentEntityCode: "",
    status: "idle"
}

export const getEntityContacts = createAsyncThunk(
    "Contact/getEntityContacts",
    async (entityId: string): Promise<DatabaseEntityContact[]> => {
        try {
            const result = await http<string, DatabaseEntityContact[]>({
                path: `/contact/getEntityContact/${entityId}`,
                method: `get`,
            });
            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            }
            else {
                return [];
            }
        } catch (ex) {
            return [];
        }
    });

export const getEntityContactByEmail = createAsyncThunk(
    "Contact/getEntityContactByEmail",
    async(entityByEmail:{ entityCode:string, email:string }): Promise<DatabaseEntityContact| null> => {
        try {
            const result = await http<string, DatabaseEntityContact>({
                path: `/contact/getEntityContactByEmail/${entityByEmail.entityCode}?email=${entityByEmail.email}`,
                method: `get`,
            });
            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            }
            else {
                return null;
            }
        } catch (ex) {
            return null;
        }


    });

export const saveEntityContact = createAsyncThunk(
    "Contact/saveEntityContact",
    async (entityContactInput: DatabaseEntityContact): Promise<DatabaseEntityContact | null> => {
        try {
            const result = await http<DatabaseEntityContact, DatabaseEntityContact>({
                path: `/contact/saveEntityContactAsync`,
                method: `post`,
                body: entityContactInput,
            });
            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            }
            else {
                return null;
            }
        } catch (ex) {
            return null;
        }
    });

export const saveEntityAuthorizationsByUser = createAsyncThunk(
    "Contact/saveEntityAuthorizationsByUser",
    async (entityContactsInput: Array<DatabaseEntityContact>): Promise<DocumentReference | null> => {
        try {
            const result = await http<Array<DatabaseEntityContact>, DocumentReference>({
                path: `/contact/saveEntityAuthorizationsByUser`,
                method: `post`,
                body: entityContactsInput,
            });
            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            }
            else {
                return null;
            }
        } catch (ex) {
            return null;
        }
    });

export const deauthorizeEntityAuthorizationsByUser = createAsyncThunk(
    "Contact/deauthorizeEntityAuthorizationsByUser",
    async (entityContactsInput: Array<DatabaseEntityContact>): Promise<DocumentReference | null> => {
        try {
            const result = await http<Array<DatabaseEntityContact>, DocumentReference>({
                path: `/contact/deauthorizeEntityAuthorizationsByUser`,
                method: `post`,
                body: entityContactsInput,
            });
            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            }
            else {
                return null;
            }
        } catch (ex) {
            return null;
        }
    });

export const deauthorizeEntityContact = createAsyncThunk(
    "Contact/deauthorizeEntityContact",
    async (userByEntity: { item1: string, item2: string }): Promise<logerUser | null> => {
        try {
            console.log(userByEntity);
            const result = await http<{ item1: string, item2: string }, logerUser>({
                path: `/registration/deauthorizeEntityAuthorization`,
                method: `post`,
                body: userByEntity,
            });
            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            }
            else {
                return null;
            }
        } catch (ex) {
            return null;
        }
    });

export const getUserEntityAuthorizations = createAsyncThunk(
    "Contact/getUserEntityAuthorizations",
    async (userId: string): Promise<Array<EntityUserAuthorization> | null> => {
        try {
           
            const result = await http<string, Array<EntityUserAuthorization>>({
                path: `/contact/getUserEntityAuthorizations`,
                method: `post`,
                body: userId,
            });
            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            }
            else {
                return null;
            }
        } catch (ex) {
            return null;
        }
    });

export const getLGEs = createAsyncThunk(
    "Contact/getLGEs",
    async (): Promise<{ label: string; value: string }[] | null> => {
        try {
            const result = await http<null, logerUser[]>({
                path: `/user/allLGEUsers`,
                method: 'get',
            });
            if (result.ok && result.parsedBody) {

                let lgeSelection: { label: string; value: string }[] =
                    [];
                for (let item of result.parsedBody) {

                    var name = (item.firstName + " " + item.lastName).trim();

                    var userSelection: { label: string; value: string } =
                    {
                        label: name,
                        value: item.login
                    };

                    lgeSelection.push(userSelection);
                }

                //TODO seeing duplicates form query, need to analyze the data
                //for now this will due
                const distinctList : any[] = [];
                const map = new Map();
                for (const item of lgeSelection) {
                    if (!map.has(item.value)) {
                        map.set(item.value, true);
                        distinctList.push({
                            label: item.label,
                            value: item.value
                        });
                    }
                }
                return distinctList;
            }
            else {
                return null;
            }
        } catch (ex) {
            return null;
        }
    });

export const getAllAdminUsers = createAsyncThunk(
    "Contact/getAllAdminUsers",
    async (): Promise<{ label: string; value: string }[] | null> => {
        try {
            const result = await http<null, logerUser[]>({
                path: `/user/allAdminUsers`,
                method: 'get',
            });
            if (result.ok && result.parsedBody) {

                let adminSelection: { label: string; value: string }[] =
                    [];
                for (let item of result.parsedBody) {

                    var name = (item.firstName + " " + item.lastName).trim();

                    var userSelection: { label: string; value: string } =
                    {
                        label: name,
                        value: item.login
                    };

                    adminSelection.push(userSelection);
                }

                //TODO seeing duplicates form query, need to analyze the data
                //for now this will due
                const distinctList : any[] = [];
                const map = new Map();
                for (const item of adminSelection) {
                    if (!map.has(item.value)) {
                        map.set(item.value, true);
                        distinctList.push({
                            label: item.label,
                            value: item.value
                        });
                    }
                }
                return distinctList;
            }
            else {
                return null;
            }
        } catch (ex) {
            return null;
        }
    });


export const getAllActiveAdmins = createAsyncThunk(
    "Contact/getAllActiveAdmins",
    async (): Promise<{ label: string; value: string }[] | null> => {
        try {
            const result = await http<null, logerUser[]>({
                path: `/user/allActiveAdmins`,
                method: 'get',
            });
            if (result.ok && result.parsedBody) {

                let adminSelection: { label: string; value: string }[] =
                    [];
                for (let item of result.parsedBody) {

                    var name = (item.firstName + " " + item.lastName).trim();

                    var userSelection: { label: string; value: string } =
                    {
                        label: name,
                        value: item.login
                    };

                    adminSelection.push(userSelection);
                }

                //TODO seeing duplicates form query, need to analyze the data
                //for now this will due
                const distinctList : any[] = [];
                const map = new Map();
                for (const item of adminSelection) {
                    if (!map.has(item.value)) {
                        map.set(item.value, true);
                        distinctList.push({
                            label: item.label,
                            value: item.value
                        });
                    }
                }
                return distinctList;
            }
            else {
                return null;
            }
        } catch (ex) {
            return null;
        }
    });

export const contactSlice = createSlice({
    name: 'Contacts',
    initialState,
    reducers: {
        clearState(state: any) {
            return initialState;
        },
        setEntityCode(state: any, action: any) {
            state.status = "loading";
            state.currentEntityCode = action.payload;
            state.status = "idle";
        },
        setEntityContactEndDate(state: any, action: any) {
            var contact = state.databaseContacts.find((entityContact: any) => entityContact.entityContactId
                == action.payload.entityContactId);
            if (contact) {
                contact.endDate = DateTime.now().toISO();
            }
        }
    },
    extraReducers: (builder: any) => {
        builder
            .addCase(getEntityContacts.pending, (state: any) => {
                state.status = "loading";
            })
            .addCase(getEntityContacts.fulfilled, (state: any, action: any) => {
                state.databaseContacts = action.payload;
                state.status = "idle";
            })
            .addCase(saveEntityContact.pending, (state: any) => {
                state.status = "loading";
            })
            .addCase(saveEntityContact.fulfilled, (state: any, action: any) => {
                if (action.payload) {
                    state.databaseContacts.push(action.payload);
                }
                state.status = "idle";
            })
            .addCase(getLGEs.pending, (state: any) => {
                state.status = "loading";
            })
            .addCase(getLGEs.fulfilled, (state: any, action: any) => {
                state.status = "idle";
            })
            .addCase(getEntityContactByEmail.pending, (state: any) => {
                state.status = "loading";
            })
            .addCase(getEntityContactByEmail.fulfilled, (state: any, action: any) => {
                state.status = "idle";
            })
            .addCase(deauthorizeEntityContact.pending, (state: any) => {
                state.status = "loading";
            })
            .addCase(deauthorizeEntityContact.fulfilled, (state: any, action: any) => {
                state.status = "idle";
            })
            .addCase(getAllActiveAdmins.pending, (state: any) => {
                state.status = "loading";
            })
            .addCase(getAllActiveAdmins.fulfilled, (state: any, action: any) => {
                state.status = "idle";
            })
            .addCase(getUserEntityAuthorizations.pending, (state: any) => {
                state.status = "loading";
            })
            .addCase(getUserEntityAuthorizations.fulfilled, (state: any, action: any) => {
                state.status = "idle";
            })
            .addCase(deauthorizeEntityAuthorizationsByUser.pending, (state: any) => {
                state.status = "loading";
            })
            .addCase(deauthorizeEntityAuthorizationsByUser.fulfilled, (state: any, action: any) => {
                state.status = "idle";
            })
    },
});

export const { clearState, setEntityCode, setEntityContactEndDate } = contactSlice.actions;

export const selectDatabaseContacts = (state: RootState) =>
    state.contacts.databaseContacts.map((contact: any) => Object.assign({}, contact));

export const selectCurrentEntityCode = (state: RootState) => state.contacts.currentEntityCode;

export default contactSlice.reducer;